import {
  handwrittenNoteApi,
  type HandwrittenNoteKey,
  type HandwrittenNoteType,
} from '@mathflat/domain/HandwrittenNote'
import type { HandwrittenNoteTypes } from '@mathflat/handwritten-note'
import axios, { HttpStatusCode } from 'axios'
import { toPng } from 'html-to-image'

import { errorHandlerService } from '~/@common/services'
import type { StudentWorksheetNoteValue } from '~/@common/services/indexedDb.service'
import { reporter } from '~/@common/utils/errorReporting/reporter'

import maxios from '../utils/maxios'

interface CasNoteParams {
  assignedId: number
  problemId: number
}

type NoteData = HandwrittenNoteTypes.NoteData

export type NoteParams = HandwrittenNoteKey

interface PresignedUrlResponse extends NoteParams {
  presignedUrl: string
}

interface SignatureWorkbookNoteParams {
  studentWorkbookRevisionId: number
  pageNumber: number
}

export interface NoteUploadItem {
  params: NoteParams
  noteData: NoteData
}

export interface NoteUploadedItem extends NoteUploadItem {
  handwrittenNoteUrl: string
}

interface Api {
  fetchNoteUrl: (params: NoteParams) => Promise<string | undefined>
  fetchNote: (params: NoteParams) => Promise<NoteData | undefined>
  fetchNoteWithUrl: (
    params: NoteParams,
  ) => Promise<{ noteData: NoteData; handwrittenNoteUrl: string } | undefined>
  uploadNotesAndGetUrls: (items: NoteUploadItem[]) => Promise<NoteUploadedItem[]>
  fetchNoteByUrl: (jsonUrl: string) => Promise<NoteData>
  findNoteByUrl: (jsonUrl: string) => Promise<NoteData | undefined>
  fetchSignatureWorkbookNote: (params: SignatureWorkbookNoteParams) => Promise<NoteData | undefined>
  uploadSignatureWorkbookNote: (
    params: SignatureWorkbookNoteParams,
    data: NoteData,
  ) => Promise<void>
  fetchAcademyGroupWorkbookNote: (
    params: SignatureWorkbookNoteParams,
  ) => Promise<NoteData | undefined>
  uploadAcademyGroupWorkbookNote: (
    params: SignatureWorkbookNoteParams,
    data: NoteData,
  ) => Promise<void>
  uploadStudentWorksheetNote: (
    noteValue: StudentWorksheetNoteValue,
    noteType: HandwrittenNoteType,
  ) => Promise<boolean>
  uploadStudentWorksheetNotes: (
    noteValues: StudentWorksheetNoteValue[],
    noteType: HandwrittenNoteType,
  ) => Promise<NoteUploadedItem[]>
  uploadStudentWorksheetPng: () => Promise<string>
  fetchCasNote: (params: CasNoteParams) => Promise<NoteData | undefined>
  uploadCasNote: (params: CasNoteParams, data: NoteData) => Promise<void>
}

export const handwrittenNoteStorage = ((): Api => {
  // 필기 캔버스 하나를 가리키는 키 — 같은 문항이라도 학생·선생님·서술형 슬롯은 서로 다른 캔버스이므로
  const _noteParamsKey = ({ id, subId, tag, type }: NoteParams) => {
    return `${id}:${subId}:${tag ?? ''}:${type}`
  }

  // 폴링하는 화면은 진입 시 이 함수로 URL 만 1회 받아두고,
  // 이후 반복 조회는 findNoteByUrl 로 S3 만 읽는다.
  const fetchNoteUrl: Api['fetchNoteUrl'] = async (params) => {
    return handwrittenNoteApi.getHandwrittenNoteUrl(params)
  }

  const fetchNote: Api['fetchNote'] = async (params) => {
    return (await fetchNoteWithUrl(params))?.noteData
  }

  const fetchNoteWithUrl: Api['fetchNoteWithUrl'] = async (params) => {
    const handwrittenNoteUrl = await fetchNoteUrl(params)

    if (!handwrittenNoteUrl) {
      return
    }

    const noteData = await findNoteByUrl(handwrittenNoteUrl)

    return noteData ? { noteData, handwrittenNoteUrl } : undefined
  }

  // 클라이언트가 계산할 수 없는 값이라 발급 결과를 들고 재사용한다
  // key 는 _noteParamsKey
  const _presignedUrlCacheMap = new Map<string, Promise<string | undefined>>()

  // 서술형 슬롯은 캐시하지 않는다 — 이 PR 범위에서 서술형을 제외해 이전과 같은 발급 빈도를 유지한다
  const _isPresignedUrlCacheable = ({ type }: NoteParams) => type !== 'STUDENT_WORKSHEET_ESSAY_SLOT'

  // 재사용 범위는 상위 식별자(학습지·시험·교재) 하나
  // 그 안에서는 문항을 옮겨다녀도 재발급하지 않고, 다른 상위 식별자로 넘어가면 이전 것을 버림
  let _presignedUrlScopeId: NoteParams['id'] | undefined

  const _enterPresignedUrlScope = (scopeId: NoteParams['id']) => {
    if (_presignedUrlScopeId === scopeId) {
      return
    }

    _presignedUrlScopeId = scopeId
    _presignedUrlCacheMap.clear()
  }

  const _fetchPresignedUrls = async (params: NoteParams[]): Promise<PresignedUrlResponse[]> => {
    // 한 호출에 담기는 노트는 id 가 모두 같다 — 서술형만 slot·문항 type 이 섞이는데 학습지 id 는 하나다
    const scopeId = params[0]?.id

    if (scopeId === undefined) {
      return []
    }

    _enterPresignedUrlScope(scopeId)

    const missed = params.filter(
      (param) =>
        !_isPresignedUrlCacheable(param) || !_presignedUrlCacheMap.has(_noteParamsKey(param)),
    )

    // 캐시하지 않는 노트는 이번 호출 안에서만 promise 를 공유한다
    const uncachedUrls = new Map<string, Promise<string | undefined>>()

    if (missed.length) {
      const issued = handwrittenNoteApi
        .issueHandwrittenNotePresignedUrls(missed)
        .then(
          (issuedUrls) =>
            new Map(
              issuedUrls.map((item) => [
                _noteParamsKey({ ...item, subId: item.subId ?? 0, tag: item.tag ?? undefined }),
                item.presignedUrl ?? undefined,
              ]),
            ),
        )

      missed.forEach((param) => {
        const key = _noteParamsKey(param)
        const presignedUrl = issued.then((issuedUrls) => issuedUrls.get(key))

        if (!_isPresignedUrlCacheable(param)) {
          uncachedUrls.set(key, presignedUrl)
          return
        }

        // 발급이 실패했거나 응답에 빠졌으면 캐시를 비워 다음 업로드에서 다시 발급받는다
        presignedUrl.then(
          (issuedUrl) => {
            if (!issuedUrl) {
              _presignedUrlCacheMap.delete(key)
            }
          },
          () => _presignedUrlCacheMap.delete(key),
        )
        _presignedUrlCacheMap.set(key, presignedUrl)
      })
    }

    // 업로드 도중 다른 학습지로 넘어가 캐시가 비워져도 이번 호출은 끝까지 가야 한다.
    // await 뒤에 캐시를 다시 읽으면 그 사이 지워져 저장이 조용히 유실되므로,
    // 동기 구간에서 promise 참조를 먼저 확보한다.
    const pending = params.map((param) => {
      const key = _noteParamsKey(param)

      return { param, presignedUrl: uncachedUrls.get(key) ?? _presignedUrlCacheMap.get(key) }
    })

    const issuedParams = await Promise.all(
      pending.map(async ({ param, presignedUrl }) => ({
        ...param,
        presignedUrl: await presignedUrl,
      })),
    )

    return issuedParams.filter(
      (param): param is PresignedUrlResponse => param.presignedUrl !== undefined,
    )
  }

  const _uploadNote = async (params: NoteParams, noteData: NoteData) =>
    (await uploadNotesAndGetUrls([{ params, noteData }])).length > 0

  const _putNote = async (url: string, noteData: NoteData) => {
    return await maxios.put<void>(url, JSON.stringify(noteData), { withoutAuth: true })
  }

  const _assertNoteJsonUrl = (jsonUrl: string) => {
    // 필기 JSON 파일만 허용
    if (!jsonUrl.toLowerCase().endsWith('.json')) {
      throw new Error(`invalid json url: ${jsonUrl}`)
    }
  }

  const fetchNoteByUrl: Api['fetchNoteByUrl'] = async (jsonUrl) => {
    _assertNoteJsonUrl(jsonUrl)

    // jsonUrl을 그대로 요청하기 위해 maxios가 아니라 axios 사용함
    const { data } = await axios.get<NoteData>(`${jsonUrl}?timestamp=${Date.now()}`)
    return data
  }

  /**
   * 신규 조회 API 의 URL 은 S3 객체 존재와 무관하게 내려오므로 403/404 가 '아직 필기 없음' 이다.
   * 그 URL 로 읽는 쪽은 이 함수를 쓴다 — fetchNoteByUrl 은 4xx 를 그대로 예외로 올린다.
   */
  const findNoteByUrl: Api['findNoteByUrl'] = async (jsonUrl) => {
    _assertNoteJsonUrl(jsonUrl)

    const { status, data } = await axios.get<NoteData>(`${jsonUrl}?timestamp=${Date.now()}`, {
      validateStatus: (responseStatus) =>
        responseStatus < HttpStatusCode.BadRequest ||
        responseStatus === HttpStatusCode.Forbidden ||
        responseStatus === HttpStatusCode.NotFound,
    })

    return status < HttpStatusCode.BadRequest ? data : undefined
  }

  const fetchSignatureWorkbookNote: Api['fetchSignatureWorkbookNote'] = async (params) => {
    const noteParams: NoteParams = {
      id: params.studentWorkbookRevisionId,
      subId: params.pageNumber,
      type: 'SIGNATURE_WORKBOOK_PAGE',
    }
    return await fetchNote(noteParams)
  }

  const uploadSignatureWorkbookNote: Api['uploadSignatureWorkbookNote'] = async (
    params,
    data: NoteData,
  ) => {
    await _uploadNote(
      {
        id: params.studentWorkbookRevisionId,
        subId: params.pageNumber,
        type: 'SIGNATURE_WORKBOOK_PAGE',
      },
      data,
    )
  }

  const fetchAcademyGroupWorkbookNote: Api['fetchAcademyGroupWorkbookNote'] = async (params) => {
    const noteParams: NoteParams = {
      id: params.studentWorkbookRevisionId,
      subId: params.pageNumber,
      type: 'ACADEMY_GROUP_WORKBOOK_PAGE',
    }
    return await fetchNote(noteParams)
  }

  const uploadAcademyGroupWorkbookNote: Api['uploadAcademyGroupWorkbookNote'] = async (
    params,
    data: NoteData,
  ) => {
    await _uploadNote(
      {
        id: params.studentWorkbookRevisionId,
        subId: params.pageNumber,
        type: 'ACADEMY_GROUP_WORKBOOK_PAGE',
      },
      data,
    )
  }

  const uploadStudentWorksheetNote: Api['uploadStudentWorksheetNote'] = async (
    noteValue,
    noteType,
  ) => {
    if (noteType !== 'STUDENT_WORKSHEET_SCORING' && noteType !== 'STUDENT_EXAM_SCORING') {
      throw new Error(`invalid noteType: ${noteType}`)
    }
    return await _uploadNote(
      {
        id: noteValue.studentWorksheetId,
        subId: noteValue.worksheetProblemId,
        type: noteType,
      },
      noteValue.noteData,
    )
  }

  const uploadStudentWorksheetNotes: Api['uploadStudentWorksheetNotes'] = async (
    noteValues,
    noteType,
  ) => {
    if (noteType !== 'STUDENT_WORKSHEET_SCORING' && noteType !== 'STUDENT_EXAM_SCORING') {
      throw new Error(`invalid noteType: ${noteType}`)
    }

    const validNoteValues = noteValues.filter((item) => item.noteData.paths.length > 0)

    if (!validNoteValues.length) {
      return []
    }

    return await uploadNotesAndGetUrls(
      validNoteValues.map(({ studentWorksheetId, worksheetProblemId, noteData }) => ({
        params: { id: studentWorksheetId, subId: worksheetProblemId, type: noteType },
        noteData,
      })),
    )
  }

  // 서술형 채점 제출은 슬롯마다 필기 URL 을 요청 바디에 담아야 한다.
  // 그래서 성공 여부만이 아니라 올라간 URL 을 돌려준다.
  const uploadNotesAndGetUrls: Api['uploadNotesAndGetUrls'] = async (items) => {
    if (!items.length) return []

    const itemMap = new Map(items.map((item) => [_noteParamsKey(item.params), item]))

    const toTargets = (responses: PresignedUrlResponse[]) =>
      responses.flatMap((response) => {
        const item = itemMap.get(_noteParamsKey(response))
        return item ? [{ item, presignedUrl: response.presignedUrl }] : []
      })

    const uploadOnce = async (targets: { item: NoteUploadItem; presignedUrl: string }[]) => {
      const results = await Promise.allSettled(
        targets.map(({ item, presignedUrl }) => _putNote(presignedUrl, item.noteData)),
      )
      const succeeded: typeof targets = []
      const failed: typeof targets = []
      results.forEach((result, index) => {
        ;(result.status === 'fulfilled' ? succeeded : failed).push(targets[index])
      })
      return { succeeded, failed }
    }

    const targets = toTargets(await _fetchPresignedUrls(items.map((item) => item.params)))
    const firstPass = await uploadOnce(targets)
    let succeeded = firstPass.succeeded

    if (firstPass.failed.length) {
      // 만료된 URL 일 수 있으니 캐시를 버리고 한 번만 재발급해 재시도한다
      const retryItems = firstPass.failed.map(({ item }) => item)
      retryItems.forEach((item) => _presignedUrlCacheMap.delete(_noteParamsKey(item.params)))
      const retried = await uploadOnce(
        toTargets(await _fetchPresignedUrls(retryItems.map((item) => item.params))),
      )
      succeeded = [...succeeded, ...retried.succeeded]
    }

    const failedCount = items.length - succeeded.length

    if (failedCount) {
      reporter.capture(
        new Error(
          `handwrittenNoteApi: uploadNotesAndGetUrls failed (${failedCount}/${items.length})`,
        ),
        { mechanism: 'manual' },
      )
    }

    return succeeded.map(({ item, presignedUrl }) => ({
      ...item,
      handwrittenNoteUrl: presignedUrl.split('?')[0],
    }))
  }

  const _uploadPng = async (url: string, file: File) => {
    return await maxios.put<void>(url, file, { withoutAuth: true })
  }

  const _dataURLtoBlob = (dataUrl: string) => {
    const arr = dataUrl.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  }

  const _blobToFile = (blob: Blob, fileName: string) => {
    return new File([blob], fileName, { type: blob.type })
  }

  const _toPngAndUpload = async (url: string) => {
    const activeSwiper = document.querySelector('.swiper-slide-active')
    const scalableContent = activeSwiper?.querySelector(
      '[data-component="mn__ScalableContent"]',
    ) as HTMLElement

    const problemArea = activeSwiper?.querySelector('.problem-area') as HTMLElement

    try {
      const dataUrl = await toPng(scalableContent ?? problemArea, {
        cacheBust: true,
        skipFonts: true,
        pixelRatio: 0.5,
      })
      const blob = _dataURLtoBlob(dataUrl)
      const file = _blobToFile(blob, 'handWritten.png')
      return await _uploadPng(url, file)
    } catch (err) {
      errorHandlerService.handle(err)
    }
  }

  const uploadStudentWorksheetPng = async () => {
    const uploadUrl = await handwrittenNoteApi.issueTempHandwrittenNotePresignedUrl()

    if (!uploadUrl) {
      throw new Error('no presignedUrlData')
    }

    await _toPngAndUpload(uploadUrl)

    return uploadUrl.split('?')[0]
  }

  const fetchCasNote: Api['fetchCasNote'] = async ({ assignedId, problemId }) => {
    return await fetchNote({
      id: assignedId,
      subId: problemId,
      type: 'ASSIGNED_CAS_SCORING',
    })
  }

  const uploadCasNote: Api['uploadCasNote'] = async ({ assignedId, problemId }, data) => {
    await _uploadNote(
      {
        id: assignedId,
        subId: problemId,
        type: 'ASSIGNED_CAS_SCORING',
      },
      data,
    )
  }

  return {
    fetchNoteUrl,
    fetchNote,
    fetchNoteWithUrl,
    uploadNotesAndGetUrls,
    fetchNoteByUrl,
    findNoteByUrl,
    fetchSignatureWorkbookNote,
    uploadSignatureWorkbookNote,
    fetchAcademyGroupWorkbookNote,
    uploadAcademyGroupWorkbookNote,
    uploadStudentWorksheetNote,
    uploadStudentWorksheetNotes,
    uploadStudentWorksheetPng,
    fetchCasNote,
    uploadCasNote,
  }
})()
